import { Grid } from '../../../../Common/Grid'
import { Button, Input } from '@fullscreendigital/carrefour-dex-fe'
import { TitleHeader } from '../../../Common/TitleHeader'
import { getInitialValues, getValidationSchema } from './helpers'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from '../../../../../../utils/api'
import { formatErrorMessage } from '../../../../../../utils/formatting'
import { useNotification } from '../../../../../../hooks'
import ImageUpload from '../../../Common/ImageUpload/ImageUpload'
import { useLocation, useNavigate } from 'react-router-dom'
import AlertModal from '../../../Common/AlertModal/AlertModal'
import { URLS } from '../../../constants'
import { CircularProgress, Paper, TableContainer } from '@mui/material'
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { useOfflineElementTool } from 'components/core-ui/Offline'

const SupportScanStep2 = () => {
  const { showNotification } = useNotification()

  const navigate = useNavigate()

  const location = useLocation()
  const { supportType, supportId, truckId } = location.state
  const main = true

  const [isMissingValuesModalOpen, setIsMissingValuesModalOpen] = useState(false)
  const [MissingValuesModalContent, setMissingValuesModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isMandatorySupportModalOpen, setIsMandatorySupportModalOpen] = useState(false)
  const [MandatorySupportModalContent, setMandatorySupportModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isDifferentNumberModalOpen, setIsDifferentNumberModalOpen] = useState(false)
  const [DifferentNumberModalContent, setDifferentNumberModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isPriorityProductsModalOpen, setIsPriorityProductsModalOpen] = useState(false)
  const [PriorityProductsModalContent, setPriorityProductsModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isSensitiveAndShortDlcModalOpen, setIsSensitiveAndShortDlcModalOpen] = useState(false)
  const [SensitiveAndShortDlcModalContent, setSensitiveAndShortDlcModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [isMoreSupportsConfirmModalOpen, setIsMoreSupportsConfirmModalOpen] = useState(false)
  const [MoreSupportsConfirmModalContent, setMoreSupportsConfirmModalContent] = useState({
    title: '',
    description: '',
    buttons: []
  })
  const [getSecondarySupport, setGetSecondarySupport] = useState(false)
  const [rows, setRows] = useState([])

  const { offlineData, deleteOfflineData, saveOfflineData } = useOfflineElementTool({
    dataType: 'SUPPORT_SCAN_STEP_2',
    url: URLS.AUTH_SUPPORT_SCAN_STEP_2,
    elementId: `${truckId}-${supportId}-${supportType}`
  })

  const handleError = error => {
    showNotification({
      message: `${formatErrorMessage(error)}`,
      type: 'error'
    })
  }

  const { data: supportTypeList, isLoading } = useQuery(
    'SupportPrincipalType',
    async () => {
      const support = await makeCoreGetRequest('/shop-reception-mobile/support/shop/principal/type')
      return support
    },
    {
      initialData: [],
      onError: handleError
    }
  )

  const validationSchema = useMemo(() => getValidationSchema(), [])

  const initialValues = useMemo(() => {
    const supportTypeWithImage = supportTypeList.map(support => ({
      ...support,
      imageUploaded: false,
      received: support.id === Number(supportType) ? 1 : support.received
    }))
    return getInitialValues(supportTypeWithImage, offlineData)
  }, [supportTypeList, offlineData])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      let totalSupportNumber = 0
      let missingValue = false
      const message = 'Există diferențe între cantitatea aşteptată şi cea descărcată pentru '
      let supportsNotOk = ''
      let uploaded: boolean[] = []

      values.supportType.forEach(support => {
        const targetSupport = values.supportType.find(support => support.id === Number(supportType))
        if (targetSupport.received === null || targetSupport.received === undefined) {
          missingValue = true
        }

        const total = Number(support.received)
        totalSupportNumber += total

        supportTypeList.forEach(st => {
          const isDefaultSupport = support.id === Number(supportType)
          const defaultReceivedUnchanged = isDefaultSupport && support.received === 1

          if (
            st.id === support.id &&
            !defaultReceivedUnchanged &&
            support.received !== null &&
            st.received !== total
          ) {
            supportsNotOk = supportsNotOk + st.name + ', '
            if (support.imageUploaded === false) uploaded.push(false)
          }
        })
      })

      if (missingValue) {
        openMissingValuesModal()
      } else if (totalSupportNumber === 0) {
        openMandatorySupportModal()
      } else if (supportsNotOk.length > 0) {
        showDifferentNumberConfirmation(values.supportType, message + supportsNotOk, uploaded)
      } else {
        await doSaveSupportTypes(values.supportType)
      }
    }
  })

  const isSubmitButtonDisabled = useMemo(
    () =>
      formik.values?.supportType.some((support, index) => {
        const initialReceived =
          support.id === Number(supportType) && support.received === 1
            ? 1
            : supportTypeList[index]?.received
        return support.received !== initialReceived && support.received && !support.imageUploaded
      }),
    [formik.values?.supportType, supportTypeList]
  )

  if (isLoading) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  const handleSuccess = async (id, file) => {
    formik.setFieldValue(`supportType[${id}].imageUploaded`, true)

    await saveOfflineData([{ field: `supportType[${id}].imageUploaded`, value: true }])

    showNotification({
      message: 'Imaginea a fost încărcată cu succes.',
      type: 'success'
    })
  }

  useEffect(() => {
    setRows(
      formik.values?.supportType.map((support, index) => ({
        id: index,
        name: support.name,
        received: support.received
      }))
    )
  }, [formik.values?.supportType])

  const doSaveSupportTypes = async values => {
    try {
      const resp = await makeCorePostRequest(
        `/shop-reception-mobile/support/shop/save/list/${main}/${truckId}/${supportId}`,
        values
      )
      if (resp.priorityProducts?.length > 0) {
        showPriorityProducts(
          resp.priorityProducts,
          resp.hasSensitiveProducts,
          resp.hasProductsWithShortDlc,
          resp.hasSgrProducts,
          resp.sgrProductsList
        )
      } else if (resp.hasSensitiveProducts || resp.hasProductsWithShortDlc || resp.hasSgrProducts) {
        showSensitiveAndShortDlcInfo(
          resp.hasSensitiveProducts,
          resp.hasProductsWithShortDlc,
          resp.hasSgrProducts,
          resp.sgrProductsList
        )
      } else {
        openMoreSupportsConfirm()
      }
      await deleteOfflineData()
    } catch (error) {
      showNotification({
        message: `Error while submitting. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    }
  }

  const showPriorityProducts = (
    priorityProducts,
    hasSensitiveProducts,
    hasProductsWithShortDlc,
    hasSgrProducts,
    sgrProducts
  ) => {
    let productsMessage = ''
    if (priorityProducts.length > 0) {
      priorityProducts.forEach(mess => {
        productsMessage = productsMessage + mess + ', '
      })
    }
    setPriorityProductsModalContent({
      title: 'Există produse prioritare pentru vânzare: ',
      description: productsMessage,
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            if (hasSensitiveProducts || hasProductsWithShortDlc || hasSgrProducts) {
              showSensitiveAndShortDlcInfo(
                hasSensitiveProducts,
                hasProductsWithShortDlc,
                hasSgrProducts,
                sgrProducts
              )
            } else {
              openMoreSupportsConfirm()
            }
            setIsPriorityProductsModalOpen(false)
          },
          color: 'primary'
        }
      ]
    })
    setIsPriorityProductsModalOpen(true)
  }

  const closePriorityProducts = () => {
    setIsPriorityProductsModalOpen(false)
  }

  const { data: secondarySupport } = useQuery(
    'deleteSecondarySupport',
    async () => {
      return await makeCoreGetRequest(
        `/shop-reception-mobile/support/shop/secondary/delete?supportId=${supportId}`,
        null
      )
    },
    {
      enabled: getSecondarySupport,
      onError: handleError,
      onSuccess: data => {
        if (data) {
          navigate(URLS.AUTH_SUPPORT_SCAN_STEP_1, { state: { item: { id: truckId } } })
        }
      }
    }
  )

  const openMoreSupportsConfirm = () => {
    setMoreSupportsConfirmModalContent({
      title: 'Adaugă alte ambalaje',
      description: '',
      buttons: [
        {
          label: 'Nu',
          onClick: () => {
            setGetSecondarySupport(true)
            setIsMoreSupportsConfirmModalOpen(false)
          },
          color: 'primary'
        },
        {
          label: 'Da',
          onClick: () => {
            setIsMoreSupportsConfirmModalOpen(false)
            navigate(URLS.AUTH_SUPPORT_SCAN_STEP_3, {
              state: {
                supportId,
                truckId
              }
            })
          },
          color: 'primary'
        }
      ]
    })

    setIsMoreSupportsConfirmModalOpen(true)
  }

  const closeMoreSupportsConfirm = () => {
    setIsMoreSupportsConfirmModalOpen(false)
  }

  const showSensitiveAndShortDlcInfo = (
    hasSensitiveProducts,
    hasShortDlcProducts,
    hasSgrProducts,
    sgrProducts
  ) => {
    let productsMessage = ''
    if (hasSensitiveProducts) {
      if (hasShortDlcProducts) {
        if (hasSgrProducts) {
          productsMessage =
            'Pe acest suport există produse sensibile, produse SGR şi produse cu DLC!'
        } else {
          productsMessage = 'Pe acest suport există produse sensibile şi produse cu DLC!'
        }
      } else {
        if (hasSgrProducts) {
          productsMessage = 'Pe acest suport există produse sensibile şi produse SGR!'
        } else {
          productsMessage = 'Pe acest suport există produse sensibile!'
        }
      }
    } else {
      if (hasShortDlcProducts) {
        if (hasSgrProducts) {
          productsMessage = 'Pe acest suport există produse cu DLC şi produse SGR!'
        } else {
          productsMessage = 'Pe acest suport există produse cu DLC!'
        }
      } else {
        if (hasSgrProducts) {
          productsMessage = 'Pe acest suport există produse SGR!'
        }
      }
    }
    let message = ''
    sgrProducts.forEach(item => {
      message += item + ', '
    })

    setSensitiveAndShortDlcModalContent({
      title: productsMessage,
      description: message,
      buttons: [
        {
          label: 'Ok',
          onClick: () => {
            setIsSensitiveAndShortDlcModalOpen(false)
            openMoreSupportsConfirm()
          },
          color: 'primary'
        }
      ]
    })
    setIsSensitiveAndShortDlcModalOpen(true)
  }

  const closeSensitiveAndShortDlc = () => {
    setIsSensitiveAndShortDlcModalOpen(false)
  }

  const openMissingValuesModal = () => {
    setMissingValuesModalContent({
      title: 'Atenție!',
      description: 'Va rugam sa completati toate valorile din ecran.',
      buttons: [
        {
          label: 'Ok',
          onClick: () => setIsMissingValuesModalOpen(false),
          color: 'primary'
        }
      ]
    })
    setIsMissingValuesModalOpen(true)
  }

  const closeMissingValuesModal = () => {
    setIsMissingValuesModalOpen(false)
  }

  const openMandatorySupportModal = () => {
    setMandatorySupportModalContent({
      title: 'Atenție!',
      description: 'Tipul de suport este obligatoriu!',
      buttons: [
        {
          label: 'Ok',
          onClick: () => setIsMandatorySupportModalOpen(false),
          color: 'primary'
        }
      ]
    })
    setIsMandatorySupportModalOpen(true)
  }

  const closeMandatorySupportModal = () => {
    setIsMissingValuesModalOpen(false)
  }

  const showDifferentNumberConfirmation = (values, message, imageUploaded) => {
    const buttons = [
      {
        label: 'Mai verific',
        onClick: () => setIsDifferentNumberModalOpen(false),
        color: 'primary'
      }
    ]

    if (!imageUploaded || imageUploaded.length === 0) {
      buttons.push({
        label: 'Am terminat',
        onClick: async () => {
          setIsDifferentNumberModalOpen(false)
          await doSaveSupportTypes(values)
        },
        color: 'primary'
      })
    }

    setDifferentNumberModalContent({
      title: message,
      description: '',
      buttons: buttons
    })
    setIsDifferentNumberModalOpen(true)
  }

  const closeDifferentNumberConfirmation = () => {
    setIsDifferentNumberModalOpen(false)
  }

  return (
    <Grid
      gridContainerStyle={{ marginTop: '20px' }}
      maxWidth="xs"
      items={[
        <TitleHeader title="Cod Suport" />,
        <TableContainer component={Paper}>
          <Table aria-label="support types table">
            <TableHead style={{ backgroundColor: '#0970E6' }}>
              <TableRow>
                <TableCell style={{ color: 'white' }}>TIP AMBALAJ</TableCell>
                <TableCell align="center" style={{ color: 'white' }}>
                  CANTITATE
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map(rowData => (
                <>
                  <TableRow key={rowData.id}>
                    <TableCell component="th" scope="row">
                      {rowData.name}
                    </TableCell>
                    <TableCell align="center">
                      <Input
                        type="number"
                        value={rowData.received}
                        onChange={e => {
                          Number((e.target as HTMLInputElement).value) >= 0 &&
                          formik.values.supportType.map(async (support, idx) => {
                            if (rowData.id === idx) {
                              formik.setFieldValue(
                                `supportType[${idx}].received`,
                                Number((e.target as HTMLInputElement).value)
                              )
                              await saveOfflineData([
                                {
                                  field: `supportType[${idx}].received`,
                                  value: Number((e.target as HTMLInputElement).value)
                                }
                              ])
                            }
                          })
                        }}
                        variant="standard"
                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      />
                    </TableCell>
                  </TableRow>
                  {supportTypeList[rowData.id].received !==
                    formik.values.supportType[rowData.id].received && (
                      <TableRow>
                        <TableCell colSpan={2}>
                          <ImageUpload
                            uploadUrl={`/shop-reception-mobile/support/shop/upload/${rowData.id}`}
                            onSuccess={(response, file) => handleSuccess(rowData.id, file)}
                            onError={handleError}
                            btnStyle={{
                              color: formik.values.supportType[rowData.id].imageUploaded ? 'green' : '#0970E6'
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>,
        <Button
          style={{ width: '100%' }}
          type="button"
          variant="contained"
          label="Confirma ambalaje"
          onClick={() => formik.handleSubmit()}
          disabled={isSubmitButtonDisabled || formik.isSubmitting}
        />,
        <AlertModal
          open={isMissingValuesModalOpen}
          handleClose={closeMissingValuesModal}
          title={MissingValuesModalContent.title}
          description={MissingValuesModalContent.description}
          buttons={MissingValuesModalContent.buttons}
        />,
        <AlertModal
          open={isMandatorySupportModalOpen}
          handleClose={closeMandatorySupportModal}
          title={MandatorySupportModalContent.title}
          description={MandatorySupportModalContent.description}
          buttons={MandatorySupportModalContent.buttons}
        />,
        <AlertModal
          open={isDifferentNumberModalOpen}
          handleClose={closeDifferentNumberConfirmation}
          title={DifferentNumberModalContent.title}
          description={DifferentNumberModalContent.description}
          buttons={DifferentNumberModalContent.buttons}
        />,
        <AlertModal
          open={isPriorityProductsModalOpen}
          handleClose={closePriorityProducts}
          title={PriorityProductsModalContent.title}
          description={PriorityProductsModalContent.description}
          buttons={PriorityProductsModalContent.buttons}
        />,
        <AlertModal
          open={isSensitiveAndShortDlcModalOpen}
          handleClose={closeSensitiveAndShortDlc}
          title={SensitiveAndShortDlcModalContent.title}
          description={SensitiveAndShortDlcModalContent.description}
          buttons={SensitiveAndShortDlcModalContent.buttons}
        />,
        <AlertModal
          open={isMoreSupportsConfirmModalOpen}
          handleClose={closeMoreSupportsConfirm}
          title={MoreSupportsConfirmModalContent.title}
          description={MoreSupportsConfirmModalContent.description}
          buttons={MoreSupportsConfirmModalContent.buttons}
        />
      ]}
      itemsPerRow={1}
    />
  )
}

export default SupportScanStep2
